import {
  api,
  CancelToken,
  getApiErrorMessage,
  normalizeListingsData,
} from "../../helpers";
import { URL_API_ALERTS } from "../../constants";

const normalizeAlert = alert => {
  let int = alert.updatedCount;

  if (typeof int === "string" || int instanceof String) {
    int = int.replace(/,/g, "");
    int = parseInt(int, 10);
  }

  alert.updatedCount = !int || isNaN(int) ? 0 : int;

  if (!!alert.query && typeof alert.query === "string") {
    try {
      const parsed = JSON.parse(alert.query);
      alert.query = parsed || null;
    } catch (e) {
      alert.query = null;
    }
  }

  return alert;
};

/**
 * Action types
 * @property {string}
 */
export const types = {
  ALERTS_CLEAR: "ALERTS_CLEAR",
  ALERTS_ERROR: "ALERTS_ERROR",
  ALERTS_LOADING: "ALERTS_LOADING",
  ALERTS_SUCCESS: "ALERTS_SUCCESS",
  ALERTS_DETAILS_ERROR: "ALERTS_DETAILS_ERROR",
  ALERTS_DETAILS_LOADING: "ALERTS_DETAILS_LOADING",
  ALERTS_DETAILS_SUCCESS: "ALERTS_DETAILS_SUCCESS",
};

/**
 * @namespace Reducers
 */
export const reducers = {
  /**
   * Updates alerts state
   * @param {array} state
   * @param {object} action
   * @param {string} action.type
   * @param {array} action.alerts
   * @memberof Reducers
   */
  alerts: (state = [], action) => {
    switch (action.type) {
      case types.ALERTS_CLEAR:
        return [];
      case types.ALERTS_SUCCESS:
        return action.alerts;
      case types.ALERTS_ERROR:
      default:
        return state;
    }
  },

  /**
   * Updates alerts error state
   * @param {string} state
   * @param {object} action
   * @param {string} action.type
   * @param {string} action.error
   * @memberof Reducers
   */
  alertsError: (state = null, action) => {
    switch (action.type) {
      case types.ALERTS_ERROR:
        return action.error;
      case types.ALERTS_LOADING:
      case types.ALERTS_SUCCESS:
        return null;
      default:
        return state;
    }
  },

  /**
   * Updates alerts loading state
   * @param {boolean} state
   * @param {object} action
   * @param {string} action.type
   * @memberof Reducers
   */
  alertsLoading: (state = false, action) => {
    switch (action.type) {
      case types.ALERTS_ERROR:
      case types.ALERTS_SUCCESS:
        return false;
      case types.ALERTS_LOADING:
        return true;
      default:
        return state;
    }
  },

  /**
   * Updates alerts details state
   * @param {array} state
   * @param {object} action
   * @param {string} action.type
   * @param {array} action.details
   * @memberof Reducers
   */
  alertsDetails: (state = [], action) => {
    switch (action.type) {
      case types.ALERTS_CLEAR:
        return [];
      case types.ALERTS_DETAILS_SUCCESS:
        return action.details;
      case types.ALERTS_DETAILS_ERROR:
      default:
        return state;
    }
  },

  /**
   * Updates alerts details error state
   * @param {string} state
   * @param {object} action
   * @param {string} action.type
   * @param {string} action.error
   * @memberof Reducers
   */
  alertsDetailsError: (state = null, action) => {
    switch (action.type) {
      case types.ALERTS_DETAILS_ERROR:
        return action.error;
      case types.ALERTS_DETAILS_LOADING:
      case types.ALERTS_DETAILS_SUCCESS:
        return null;
      default:
        return state;
    }
  },

  /**
   * Updates alerts details loading state
   * @param {boolean} state
   * @param {object} action
   * @param {string} action.type
   * @memberof Reducers
   */
  alertsDetailsLoading: (state = false, action) => {
    switch (action.type) {
      case types.ALERTS_DETAILS_ERROR:
      case types.ALERTS_DETAILS_SUCCESS:
        return false;
      case types.ALERTS_DETAILS_LOADING:
        return true;
      default:
        return state;
    }
  },
};

/**
 * @namespace Actions
 */
export const actions = {
  /**
   * Cancel alerts API request
   * * Updated with a new Axios cancel token after an API request is initiated
   * @memberof Actions
   */
  cancelAlerts: () => false,

  /**
   * Clear cached alerts
   * @memberof Actions
   */
  clear: () => ({
    type: types.ALERTS_CLEAR,
  }),

  fetchAlerts: () => (dispatch, getState) => {
    const { alertsLoading } = getState();

    if (alertsLoading) {
      actions.cancelAlerts("Canceled");
    }

    dispatch(actions.handleAlertsLoading());

    api
      .get(URL_API_ALERTS, {
        cancelToken: new CancelToken(c => {
          actions.cancelAlerts = c;
        }),
      })
      .then(response => {
        if (!!response && !!response.data && !!response.data.data) {
          const { data } = response.data;
          const normalized = data.map(alert => normalizeAlert(alert));

          dispatch(actions.handleAlertsSuccess(normalized));
        } else {
          dispatch(actions.handleAlertsError(getApiErrorMessage(response)));
        }
      })
      .catch(error => {
        if (!!error.message && error.message === "Canceled") {
          return false;
        }

        dispatch(actions.handleAlertsError(getApiErrorMessage(error)));
      });
  },

  /**
   * Handle alerts error messages
   * @param {string} message
   * @memberof Actions
   */
  handleAlertsError: message => ({
    type: types.ALERTS_ERROR,
    error: message,
  }),

  /**
   * Handle alerts loading events
   * @memberof Actions
   */
  handleAlertsLoading: () => ({
    type: types.ALERTS_LOADING,
  }),

  /**
   * Handle alerts success
   * @param {array} alerts
   * @memberof Actions
   */
  handleAlertsSuccess: alerts => ({
    type: types.ALERTS_SUCCESS,
    alerts: alerts,
  }),

  remove: id => (dispatch, getState) => {
    const { alerts, alertsDetails } = getState();
    const nextAlerts = alerts.filter(alert => alert.id !== id);
    const nextAlertsDetails = alertsDetails.filter(
      details => details.savedAlert.id !== id
    );

    dispatch(actions.handleAlertsSuccess(nextAlerts));
    dispatch(actions.handleAlertsDetailsSuccess(nextAlertsDetails));
  },

  /**
   * Cancel alerts details API request
   * * Updated with a new Axios cancel token after an API request is initiated
   * @memberof Actions
   */
  cancelAlertsDetails: () => false,

  fetchAlertsDetails: id => (dispatch, getState) => {
    const { alertsDetails, alertsDetailsLoading } = getState();

    if (alertsDetailsLoading) {
      actions.cancelAlertsDetails("Canceled");
    }

    dispatch(actions.handleAlertsDetailsLoading());

    api
      .get(`${URL_API_ALERTS}/${id}`, {
        cancelToken: new CancelToken(c => {
          actions.cancelAlertsDetails = c;
        }),
      })
      .then(response => {
        if (
          !!response &&
          !!response.data &&
          response.data.success &&
          !!response.data.data
        ) {
          const { data } = response.data;

          data.savedAlert = normalizeAlert(data.savedAlert);
          data.listings = normalizeListingsData(data.listings);

          if (
            !data.savedAlert ||
            (!!data.savedAlert &&
              !data.savedAlert.id &&
              data.savedAlert.id !== 0)
          ) {
            dispatch(actions.handleAlertsDetailsError("Unexpected response"));
          } else {
            const update = alertsDetails
              .filter(alert => alert.savedAlert.id !== data.savedAlert.id)
              .concat([data]);

            dispatch(actions.handleAlertsDetailsSuccess(update));
          }
        } else {
          dispatch(
            actions.handleAlertsDetailsError(getApiErrorMessage(response))
          );
        }
      })
      .catch(error => {
        if (!!error.message && error.message === "Canceled") {
          return false;
        }

        dispatch(actions.handleAlertsDetailsError(getApiErrorMessage(error)));
      });
  },

  /**
   * Handle alerts details error messages
   * @param {string} message
   * @memberof Actions
   */
  handleAlertsDetailsError: message => ({
    type: types.ALERTS_DETAILS_ERROR,
    error: message,
  }),

  /**
   * Handle alerts details loading events
   * @memberof Actions
   */
  handleAlertsDetailsLoading: () => ({
    type: types.ALERTS_DETAILS_LOADING,
  }),

  /**
   * Handle alerts details success
   * @param {array} alerts
   * @memberof Actions
   */
  handleAlertsDetailsSuccess: details => ({
    type: types.ALERTS_DETAILS_SUCCESS,
    details: details,
  }),
};
